import { useCallback, useEffect, useRef, useState } from 'react';
import { env } from '@/shared/config/env';
import { isEmbedded } from '@/shared/config/embedded-context';
import { endpoints } from '@/shared/services/endpoints';
import { useAuthStore } from '@/shared/stores/auth-store';
import { useUIStore } from '@/shared/stores/ui-store';
import type { AdAccount } from '@/shared/types/entities';

import { useAccounts } from '../api/accounts-queries';
import { OAUTH_RETURN_PARAM, subscribeOAuthReturn } from '../logic/oauth-return-channel';
import { platformToPathSegment } from '../logic/platform-path';

const POLL_INTERVAL_MS = 2000;
const CONNECT_TIMEOUT_MS = 3 * 60 * 1000;
// Grace period after the tab returns, in case the account lands a moment later.
const RETURN_GRACE_MS = 6000;

function buildConnectUrl(platform: string, orgId: string): string {
  // Embedded (Module Federation): the memory router keeps the host URL as-is, so
  // return to the host page that started OAuth instead of the standalone app URL.
  const base = isEmbedded() ? window.location.href : `${env.VITE_APP_URL}promote/accounts`;
  // The backend redirects here verbatim and adds no status of its own, so carry a
  // marker the returning tab can recognise (see use-oauth-return).
  const returnTo = new URL(base);
  returnTo.searchParams.set(OAUTH_RETURN_PARAM, platform);
  return (
    `${env.VITE_API_BASE_URL}${endpoints.accounts.connect(platformToPathSegment(platform))}` +
    `?organizationId=${encodeURIComponent(orgId)}` +
    `&redirectUrl=${encodeURIComponent(returnTo.toString())}`
  );
}

/**
 * Fingerprint of a platform's accounts. Re-authorizing an already-connected
 * account updates the existing row instead of adding one, so comparing ids alone
 * would never register the change — token expiry and status carry it.
 */
function platformAccountsSignature(accounts: AdAccount[] | undefined, platform: string): string {
  return (accounts ?? [])
    .filter((account) => account.platform === platform)
    .map((account) => `${account.id}:${account.status}:${account.tokenExpiresAt ?? ''}`)
    .sort()
    .join('|');
}

export interface UseOAuthConnectResult {
  /** Platform currently awaiting authorization in the OAuth tab, if any */
  connectingPlatform: string | null;
  startConnect: (platform: string) => void;
  cancelConnect: () => void;
}

/**
 * Runs the platform OAuth flow in a separate browser tab so the user keeps their
 * place in Promote. The returning tab announces itself over a BroadcastChannel;
 * a slower account-data poll covers the cases that produce no message.
 */
export function useOAuthConnect(): UseOAuthConnectResult {
  const orgId = useAuthStore((state) => state.orgId);
  const addToast = useUIStore((state) => state.addToast);
  const { data: accounts, refetch } = useAccounts();

  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const baselineRef = useRef('');
  const startedAtRef = useRef(0);
  const returnedAtRef = useRef(0);
  // Flipped synchronously so a poll already awaiting a refetch cannot report back
  // after the flow was cancelled or resolved.
  const pendingRef = useRef(false);
  // The channel callback and the interval both call check(); one refetch at a time.
  const checkingRef = useRef(false);

  // Best-effort: after COOP severs the opener the handle is inert, so the OAuth
  // tab closes itself on return and this only matters when the user cancels.
  const closePopup = useCallback(() => {
    try {
      popupRef.current?.close();
    } catch {
      /* opener relationship already gone */
    }
    popupRef.current = null;
  }, []);

  const startConnect = useCallback(
    (platform: string) => {
      if (!orgId) return;
      const connectUrl = buildConnectUrl(platform, orgId);
      // Must open synchronously from the click to survive popup blockers, and without
      // window features so browsers give us a tab rather than a popup window.
      const popup = window.open(connectUrl, '_blank');

      if (!popup) {
        // Blocked: fall back to authorizing in the current tab.
        window.location.href = connectUrl;
        return;
      }

      popup.focus();
      popupRef.current = popup;
      baselineRef.current = platformAccountsSignature(accounts, platform);
      startedAtRef.current = Date.now();
      returnedAtRef.current = 0;
      pendingRef.current = true;
      setConnectingPlatform(platform);
    },
    [accounts, orgId],
  );

  const cancelConnect = useCallback(() => {
    pendingRef.current = false;
    closePopup();
    setConnectingPlatform(null);
  }, [closePopup]);

  useEffect(() => {
    if (!connectingPlatform) return;

    const finish = (message: string, variant: 'success' | 'error' | 'warning') => {
      pendingRef.current = false;
      closePopup();
      setConnectingPlatform(null);
      addToast({ message, variant });
    };

    const check = async () => {
      if (checkingRef.current) return;
      checkingRef.current = true;
      try {
        await runCheck();
      } finally {
        checkingRef.current = false;
      }
    };

    const runCheck = async () => {
      const result = await refetch();
      if (!pendingRef.current) return;

      if (platformAccountsSignature(result.data, connectingPlatform) !== baselineRef.current) {
        finish('Account connected successfully', 'success');
        return;
      }

      // Returning without the account showing up is a failure, not a success —
      // the platform callback may have stored it somewhere this app can't read.
      if (returnedAtRef.current && Date.now() - returnedAtRef.current > RETURN_GRACE_MS) {
        finish('Authorization finished, but no account was added. Please try again.', 'warning');
        return;
      }

      if (Date.now() - startedAtRef.current > CONNECT_TIMEOUT_MS) {
        finish('Connection timed out. Please try again.', 'error');
      }
    };

    // Primary signal: the returning tab announces itself on the channel.
    const unsubscribe = subscribeOAuthReturn(() => {
      if (!returnedAtRef.current) returnedAtRef.current = Date.now();
      void check();
    });

    // Fallback for flows we get no message from (another browser profile, or a
    // tab the user navigated away), plus the post-return grace window.
    const interval = setInterval(() => void check(), POLL_INTERVAL_MS);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [addToast, closePopup, connectingPlatform, refetch]);

  return { connectingPlatform, startConnect, cancelConnect };
}
